import {
  Alert, Badge, Button, Container, Fieldset, Grid, Group, LoadingOverlay,
  Modal, PasswordInput, Select, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import {
  IconAlertCircle, IconArrowLeft, IconKey, IconLogout,
  IconRotate, IconUserOff,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../api/client';
// Task #155 (2026-06-02): surface backend password-policy 422 errors
// inline on the password field instead of as a generic alert.
import { getPasswordPolicyErrors } from '../utils/applyApiError';
import { useAlertModal } from '../components/AlertModalProvider';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { useCurrentUser } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
// Task #255 (2026-06-04): wire the user create/update forms into the
// global dirty-form registry so sidebar nav, browser back, logout, and
// tab close all prompt before discarding unsaved edits. Gap discovered
// during #251 Stage 2 testing.
import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
// #276 (2026-06-08): Named RM assignments section at the bottom of
// UserDetailPage. Read-only — writes are managed on each Agreement's
// Named RMs tab.
import { useAgreementUsers } from '../hooks/useAgreementUsers';
import { AgreementRef } from '../components/EntityRef';
import type { UserCreate, UserRole, UserUpdate } from '../api/types';
import {
  useAdminPasswordReset, useCreateUser, useDeleteUser, useForceLogout,
  useRestoreUser, useUpdateUser, useUser,
} from '../hooks/useUsers';

interface CreateFormValues {
  email: string;
  display_name: string;
  password: string;
  role: UserRole;
}

interface UpdateFormValues {
  display_name: string;
  role: UserRole;
}

// Phase B1 role taxonomy. 'staff' is legacy and kept selectable so existing
// users can be loaded into the form without validation errors; new users
// should pick from RM / Compliance / ReadOnly / Admin.
const ROLE_OPTS = [
  { value: 'rm',         label: 'RM (Relationship Manager)' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'read_only',  label: 'Read-only (auditor / view-only)' },
  { value: 'admin',      label: 'Admin' },
  { value: 'staff',      label: 'Staff (legacy)' },
];

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const { data: user, isLoading, error: loadError } = useUser(isNew ? undefined : id);
  const create = useCreateUser();
  const update = useUpdateUser(id ?? '');
  const del = useDeleteUser();
  const restore = useRestoreUser();
  const resetPw = useAdminPasswordReset(id ?? '');
  const forceLogout = useForceLogout(id ?? '');
  const { showAlert } = useAlertModal();
  const { user: currentUser } = useCurrentUser();
  const { can } = usePermissions();

  const [resetModalOpen, resetModalCtl] = useDisclosure(false);
  const [forceLogoutModalOpen, forceLogoutModalCtl] = useDisclosure(false);
  const [disableModalOpen, disableModalCtl] = useDisclosure(false);
  const [disableConfirmEmail, setDisableConfirmEmail] = useState('');

  // Self-protection (Pedro 2026-05-04 round): when an admin is viewing
  // their own record, hide all destructive actions. The corresponding
  // server-side guards in routers/users.py reject these requests
  // anyway (cannot_delete_self, would_leave_zero_admins), but the
  // frontend hiding makes the UI honest about what's allowed.
  const isViewingSelf = !isNew && user && currentUser?.id === user.id;
  const isAdmin = currentUser?.role === 'admin';
  // The three destructive actions are visible only to admins, viewing
  // other (non-self) alive users. Self gets a banner instead (rendered
  // in the form body below).
  const canActOnUser = isAdmin && !isNew && user && !isViewingSelf && !user.deleted_at;
  // #14 P2: force-logout gates on the dedicated permission (seeded admin-only,
  // so behaviour-preserving). The other destructive user actions (create /
  // delete / password-reset) stay role-based via canActOnUser — no permission
  // key exists for them yet.
  const canForceLogout = can('user.force_logout') && !isNew && user && !isViewingSelf && !user.deleted_at;

  // Two separate forms: create needs (email + password); update doesn't.
  const createForm = useForm<CreateFormValues>({
    initialValues: { email: '', display_name: '', password: '', role: 'staff' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Valid email required'),
      display_name: (v) => (v.trim() ? null : 'Required'),
      password: (v) => (v.length >= 12 ? null : 'Min 12 characters'),
    },
  });

  const updateForm = useForm<UpdateFormValues>({
    initialValues: { display_name: user?.display_name ?? '', role: (user?.role ?? 'staff') as UserRole },
    validate: { display_name: (v) => (v.trim() ? null : 'Required') },
  });

  // Re-sync update form when the user loads
  if (user && !isNew && updateForm.values.display_name === '' && user.display_name) {
    updateForm.setValues({ display_name: user.display_name, role: user.role });
    updateForm.resetDirty({ display_name: user.display_name, role: user.role });
  }

  const resetPwForm = useForm<{ new_password: string }>({
    initialValues: { new_password: '' },
    validate: {
      new_password: (v) => (v.length >= 12 ? null : 'Min 12 characters'),
    },
  });

  // Task #192 (2026-06-04): Tier-2 inline alert pattern for the main user
  // form (create or update flow). Field-level errors continue to route via
  // getPasswordPolicyErrors → setFieldError (Task #155). Non-field errors
  // surface here as an inline banner above the form body — same idiom as
  // Account/Person/Agreement detail pages. The reset-password flow lives
  // inside a modal and keeps Tier-3 modal feedback per convention edge case.
  const _userFormErr = isNew ? create.error : update.error;
  const userFormApiError =
    _userFormErr instanceof ApiError && !getPasswordPolicyErrors(_userFormErr)
      ? _userFormErr.detail
      : null;

  // Task #255 (2026-06-04): publish the active main form's dirty state
  // to the global dirty-form registry. The hook installs:
  //   - useBlocker (sidebar nav / back arrow / Link clicks fire the
  //     unsaved-changes modal)
  //   - window beforeunload (tab close / hard reload warn)
  //   - registerDirty (AppShell.guardedLogout sees anyDirty()=true)
  // resetPwForm is intentionally NOT registered — it lives inside a
  // Mantine Modal, and modal-close itself acts as the discard UX
  // (Edge case 6 in the alert consistency convention).
  //
  // MUST run before the loadError early-return: hooks must execute
  // unconditionally in the same order on every render.
  const activeFormIsDirty = isNew ? createForm.isDirty() : updateForm.isDirty();
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('user-update-form', activeFormIsDirty, !isNew);
  useDirtyFormGuard(activeFormIsDirty, saveForGuard);

  // #4 (§3h): /users/* is admins-only. On a direct-URL by a non-admin, show the
  // single uniform access-denied message instead of the raw backend 403 detail.
  if (currentUser && !isAdmin) {
    return <AccessDeniedAlert />;
  }

  if (loadError && !isNew) {
    return (
      <Container size="xl">
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {loadError.detail}
        </Alert>
      </Container>
    );
  }

  function onCreate(values: CreateFormValues) {
    const payload: UserCreate = {
      email: values.email.trim(),
      display_name: values.display_name.trim(),
      password: values.password,
      role: values.role,
    };
    create.mutate(payload, {
      onSuccess: (u) => {
        // Task #255 (2026-06-04): mark the form clean BEFORE navigating so
        // the useBlocker dirty-form-guard doesn't fire its "Discard & leave?"
        // modal on the post-success navigate. Latent bug — pre-existed but
        // was harmless until #255 wired UserDetailPage into the registry.
        createForm.resetDirty();
        notifications.show({ message: `User ${u.email} created`, color: 'green' });
        navigate(`/users/${u.id}`, { replace: true });
      },
      onError: (e) => {
        // Task #155 (2026-06-02): backend 422 password_policy_violation
        // → render the rules inline on the password field instead of
        // the generic "Could not create user" alert. The structured
        // detail (one message per rule) is far more actionable than
        // showing "[object Object]" or a single conflated string.
        const pwErrors = getPasswordPolicyErrors(e);
        if (pwErrors) {
          createForm.setFieldError('password', pwErrors.join(' '));
          return;
        }
        // Task #192 (2026-06-04): non-field errors surface inline via
        // userFormApiError (banner rendered above the form body), not as a
        // modal. Tier 2 per the alert consistency convention.
      },
    });
  }

  function onUpdate(values: UpdateFormValues) {
    const payload: UserUpdate = {
      display_name: values.display_name.trim(),
      role: values.role,
    };
    update.mutate(payload, {
      onSuccess: () => {
        // Task #255 (2026-06-04): mark the form clean against the values we
        // just submitted. Without this, Mantine's form.isDirty() keeps
        // returning true (initial snapshot vs. current still differs), the
        // dirty-form-guard registry stays true, and useBlocker fires the
        // "Discard & leave?" modal on every subsequent navigation — even
        // though the save persisted. Matches the idiom used in
        // Person/Account/Agreement detail pages. Latent bug — pre-existed
        // but was invisible until #255 wired UserDetailPage into the
        // registry.
        updateForm.resetDirty(values);
        notifications.show({ message: 'User updated', color: 'green' });
      },
      // Task #192 (2026-06-04): surfaced inline via userFormApiError per
      // the alert consistency convention (Tier 2 in-page banner).
      onError: () => { /* see userFormApiError above */ },
    });
  }

  function onResetPassword(values: { new_password: string }) {
    if (!id) return;
    resetPw.mutate(values, {
      onSuccess: () => {
        notifications.show({
          message: 'Password reset. Communicate the new password to the user out-of-band.',
          color: 'green',
          autoClose: 10000,
        });
        resetModalCtl.close();
        resetPwForm.reset();
      },
      onError: (e: ApiError) => {
        // Task #155 (2026-06-02): same inline-policy-error treatment
        // for the admin reset-password flow.
        const pwErrors = getPasswordPolicyErrors(e);
        if (pwErrors) {
          resetPwForm.setFieldError('new_password', pwErrors.join(' '));
          return;
        }
        showAlert({ color: 'red', title: 'Could not reset password', message: e.detail });
      },
    });
  }

  return (
    <Container size="xl">
      <Stack pos="relative">
        <LoadingOverlay visible={!isNew && isLoading} />

        <Group justify="space-between">
          <Group>
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate(-1)}>
              Back to users
            </Button>
            <Title order={2}>{isNew ? 'New user' : user?.display_name ?? 'User'}</Title>
          </Group>
          {!isNew && user && (
            <Group gap="xs">
              {/* Three-button gravity (Pedro 2026-05-04): left → right
                  ascending severity, with green/yellow/red shading and
                  hover tooltips clarifying what each button does. The
                  self-banner in the form body explains why these are
                  hidden when an admin views their own record. */}
              {canForceLogout && (
                <Tooltip
                  label="End all of this user's current sessions. Their password is unchanged; they can sign in again immediately."
                  withArrow position="bottom" multiline w={280}
                >
                  <Button leftSection={<IconLogout size={16} />} variant="light"
                          color="green"
                          onClick={forceLogoutModalCtl.open}>
                    Sign out everywhere
                  </Button>
                </Tooltip>
              )}
              {canActOnUser && (
                <Tooltip
                  // Pedro 2026-05-04 round-4: the button label needs to
                  // convey BOTH effects of the action (password change +
                  // forced sign-out) so an admin doesn't reach for it
                  // expecting only one of them.
                  label="Set a new password AND end all current sessions. The user must use the new password to sign in again on every device."
                  withArrow position="bottom" multiline w={280}
                >
                  <Button leftSection={<IconKey size={16} />} variant="light"
                          color="yellow"
                          onClick={resetModalCtl.open}>
                    Reset password & sign out
                  </Button>
                </Tooltip>
              )}
              {canActOnUser && (
                <Tooltip
                  label="Permanently revoke access. End all current sessions and prevent future logins. Recoverable by another admin within 30 days."
                  withArrow position="bottom" multiline w={280}
                >
                  <Button leftSection={<IconUserOff size={16} />} variant="light"
                          color="red"
                          onClick={() => {
                            setDisableConfirmEmail('');
                            disableModalCtl.open();
                          }}>
                    Disable user
                  </Button>
                </Tooltip>
              )}
              {/* Restore is the inverse of Disable — only shown for
                  soft-deleted users. Sits on its own because the three-
                  button gravity row only applies to alive users. */}
              {!isNew && user.deleted_at && isAdmin && (
                <Tooltip
                  label="Restore this user. They can sign in again with their existing password."
                  withArrow position="bottom" multiline w={280}
                >
                  <Button leftSection={<IconRotate size={16} />} color="blue"
                          variant="light"
                          onClick={() => restore.mutate(user.id, {
                            onSuccess: () => notifications.show({
                              message: `${user.email} restored`,
                              color: 'green',
                            }),
                          })}>
                    Restore
                  </Button>
                </Tooltip>
              )}
            </Group>
          )}
        </Group>

        {userFormApiError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {userFormApiError}
          </Alert>
        )}

        {isNew ? (
          <form onSubmit={createForm.onSubmit(onCreate)}>
            <Fieldset legend="Account details">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Email" required {...createForm.getInputProps('email')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Display name" required {...createForm.getInputProps('display_name')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select label="Role" data={ROLE_OPTS} required allowDeselect={false}
                          {...createForm.getInputProps('role')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <PasswordInput label="Initial password" required
                                 description="≥12 characters. Communicate to the user out-of-band; they should change it on first login."
                                 {...createForm.getInputProps('password')} />
                </Grid.Col>
              </Grid>
            </Fieldset>
            <Group justify="flex-end" mt="md">
              <Button type="submit" loading={create.isPending}>Create user</Button>
            </Group>
          </form>
        ) : user && (
          <form id="user-update-form" onSubmit={updateForm.onSubmit(onUpdate)}>
            <Fieldset legend="Account details">
              <Grid>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Email" value={user.email} disabled
                             description="Email is immutable. Soft-delete and recreate to change."/>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Display name" required {...updateForm.getInputProps('display_name')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Select label="Role" data={ROLE_OPTS} required allowDeselect={false}
                          {...updateForm.getInputProps('role')} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput label="Last login"
                             value={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'never'}
                             disabled />
                </Grid.Col>
              </Grid>
            </Fieldset>
            <Group justify="flex-end" mt="md">
              <Button type="submit" loading={update.isPending}
                      disabled={!updateForm.isDirty()}>
                Save changes
              </Button>
            </Group>
          </form>
        )}

        {!isNew && user?.deleted_at && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />}>
            This user is soft-deleted. They cannot log in. You can restore them
            within the 30-day window.
          </Alert>
        )}

        {/* Self-protection banner: shown when an admin is viewing their
            own record. The destructive action buttons are hidden in
            this case (see canActOnUser above); this banner explains
            why. The corresponding server-side guards in
            backend/app/routers/users.py reject the requests anyway,
            so the protection is defence-in-depth, not just UI hiding. */}
        {isViewingSelf && isAdmin && (
          <Alert color="blue" icon={<IconAlertCircle size={16} />}
                 title="This is your own account">
            Destructive actions (sign out everywhere, reset password, disable)
            are hidden when viewing your own user record. Ask another
            administrator if you genuinely need one of these performed on
            your account. You can still edit your display name and change
            your own password through the regular settings flow.
          </Alert>
        )}

        {/* #276 (2026-06-08): Named RM assignments for this user across
            the Agreement book. Read-only display — assignments are
            managed from each Agreement's Named RMs tab. */}
        {!isNew && user && (
          <Fieldset legend="Named RM assignments">
            <UserAgreementAssignments userId={user.id} />
          </Fieldset>
        )}
      </Stack>

      {/* MIDDLE GRAVITY (yellow): Reset password.
          Sets a new password AND ends every active session (the
          backend bumps session_epoch on password change). The user
          must use the new password to sign in. */}
      <Modal opened={resetModalOpen} onClose={resetModalCtl.close}
             title="Reset password" size="md">
        <form onSubmit={resetPwForm.onSubmit(onResetPassword)}>
          <Stack>
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              You are about to set a new password for <b>{user?.email}</b> and
              end every active session they currently have. They will be signed
              out immediately on every device and must use the new password
              to sign in again.
            </Alert>
            <Text size="sm" c="dimmed">
              The user will not receive an automatic notification — communicate
              the new password to them out-of-band, and instruct them to change
              it themselves on first login. This event is recorded in the
              audit log.
            </Text>
            <PasswordInput label="New password" required
                           description="≥12 characters. Communicate to the user out-of-band."
                           {...resetPwForm.getInputProps('new_password')} />
            <Group justify="flex-end">
              <Button variant="default" onClick={resetModalCtl.close}>Cancel</Button>
              <Button type="submit" color="yellow" loading={resetPw.isPending}>
                Reset password
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* LEFT GRAVITY (green): Sign out everywhere.
          Bumps session_epoch only. Password unchanged. User can sign
          back in immediately with their existing credentials. */}
      <Modal opened={forceLogoutModalOpen} onClose={forceLogoutModalCtl.close}
             title="Sign out everywhere" size="md">
        <Stack>
          <Alert color="green" icon={<IconAlertCircle size={16} />}>
            You are about to end every active session for <b>{user?.email}</b>.
            They will see a 401 on their next request and be redirected to
            the login page.
          </Alert>
          <Text size="sm" c="dimmed">
            Their password is unchanged — they can sign back in immediately
            with their existing credentials. Use this for: role changes
            (so the user picks up new permissions on a fresh login),
            suspected session compromise, or generic "log this user out
            of every device" hygiene. This event is recorded in the audit
            log.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={forceLogoutModalCtl.close}>
              Cancel
            </Button>
            <Button color="green" loading={forceLogout.isPending}
                    onClick={() => {
                      forceLogout.mutate(undefined, {
                        onSuccess: () => {
                          notifications.show({
                            message: `${user?.email} has been signed out from every device`,
                            color: 'green',
                          });
                          forceLogoutModalCtl.close();
                        },
                        onError: (e: ApiError) => showAlert({
                          color: 'red',
                          title: 'Could not sign user out',
                          message: e.detail,
                        }),
                      });
                    }}>
              Sign them out
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* RIGHT GRAVITY (red): Disable user.
          Soft-delete + immediate session invalidation (via the
          deleted_at filter in the auth dependency). User cannot log
          back in. Type-the-email guard prevents misclick disasters,
          per Pedro's request 2026-05-04. The corresponding backend
          guard at routers/users.py refuses self-disable and
          last-admin-disable regardless of what gets clicked here. */}
      <Modal opened={disableModalOpen}
             onClose={() => { disableModalCtl.close(); setDisableConfirmEmail(''); }}
             title="Disable user" size="md">
        <Stack>
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            You are about to <b>disable</b> <b>{user?.email}</b> permanently.
            Every active session ends immediately. They cannot sign in again
            unless an administrator restores them within the 30-day window.
            Their record is preserved for the audit trail.
          </Alert>
          <Text size="sm" c="dimmed">
            Use this for: confirmed malicious user, employee offboarding,
            account closure. Their password is preserved on disk but unusable
            because the user record becomes invisible to authentication.
            This event is recorded in the audit log.
          </Text>
          <TextInput
            label={<>To confirm, type the email <b>{user?.email}</b> below</>}
            value={disableConfirmEmail}
            onChange={(e) => setDisableConfirmEmail(e.currentTarget.value)}
            placeholder={user?.email ?? ''}
            autoComplete="off"
          />
          <Group justify="flex-end">
            <Button variant="default"
                    onClick={() => { disableModalCtl.close(); setDisableConfirmEmail(''); }}>
              Cancel
            </Button>
            <Button color="red" loading={del.isPending}
                    disabled={disableConfirmEmail.trim() !== user?.email}
                    onClick={() => {
                      if (!user) return;
                      del.mutate(user.id, {
                        onSuccess: () => {
                          notifications.show({
                            message: `${user.email} has been disabled`,
                            color: 'green',
                          });
                          disableModalCtl.close();
                          setDisableConfirmEmail('');
                          navigate('/users');
                        },
                        onError: (e: ApiError) => showAlert({
                          color: 'red',
                          title: 'Could not disable user',
                          // Backend self-protection guards emit a structured
                          // detail object with code + message; e.detail is
                          // already that message string.
                          message: e.detail,
                        }),
                      });
                    }}>
              Disable user
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// UserAgreementAssignments — read-only list of agreements this user is a
// Named RM on (#276, 2026-06-08).
//
// Lives at the bottom of UserDetailPage as a Fieldset. Reads
// agreement_users filtered by user_id; renders each row as a link to
// the Agreement (where assignments are managed). To change tier or
// revoke, the user/admin opens the Agreement's Named RMs tab —
// keeping a single source-of-truth surface for writes.
// ──────────────────────────────────────────────────────────────────────────
function UserAgreementAssignments({ userId }: { userId: string }) {
  const assignmentsQ = useAgreementUsers({ user_id: userId, limit: 200 });
  const rows = assignmentsQ.data?.items ?? [];

  if (assignmentsQ.isLoading) {
    return <Text c="dimmed" size="sm">Loading…</Text>;
  }
  if (rows.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        This user is not assigned as Named RM on any Agreement.
      </Text>
    );
  }

  return (
    <Table withTableBorder striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Agreement</Table.Th>
          <Table.Th w={140}>Tier</Table.Th>
          <Table.Th w={180}>Assigned</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((a) => (
          <Table.Tr key={a.id}>
            <Table.Td>
              <AgreementRef id={a.agreement_id} linkPathPrefix="/agreements" />
            </Table.Td>
            <Table.Td>
              {a.is_primary
                ? <Badge color="yellow" variant="filled">Primary</Badge>
                : <Badge color="gray" variant="light">Secondary</Badge>}
            </Table.Td>
            <Table.Td>
              <Text size="xs" c="dimmed">
                {new Date(a.assigned_at).toLocaleString()}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
